import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { seeded, observeInView } from '../../lib/living';

// Pictures this session has already shown appear instantly when they remount.
const shown = new Set();
const remember = (src) => {
    if (!src) return;
    shown.delete(src);
    shown.add(src);
    if (shown.size > 256) shown.delete(shown.values().next().value);
};

/* A photograph that breathes: it fades in over a calm placeholder, then drifts
 * slowly (ken burns) with a passing light sheen. The motion only runs while
 * the picture is on screen and holds still while the page scrolls, so long
 * lists stay smooth. `still` keeps it static.
 */
export default function LivingImage({ src, alt = '', className = '', imgClassName = '', fit = 'cover', still = false, eager = false, onLoad, onError, ...rest }) {
    const wrap = useRef(null);
    const img = useRef(null);
    const [status, setStatus] = useState(() => ({ src, loaded: shown.has(src), failed: false }));
    const loaded = status.src === src ? status.loaded : shown.has(src);
    const failed = status.src === src && status.failed;
    const [live, setLive] = useState(false);

    const style = useMemo(() => {
        const r = seeded(src || alt);
        return {
            '--kb-dur': `${(16 + r() * 10).toFixed(1)}s`,
            '--kb-delay': `${(-r() * 12).toFixed(1)}s`,
            '--kb-x': `${((r() - 0.5) * 6).toFixed(2)}%`,
            '--kb-y': `${((r() - 0.5) * 6).toFixed(2)}%`,
            '--sheen-delay': `${(r() * 8).toFixed(1)}s`,
        };
    }, [src, alt]);

    // Already in the browser cache: skip the fade so nothing blinks.
    useLayoutEffect(() => {
        const el = img.current;
        const cached = Boolean(el?.complete && el.naturalWidth);
        if (cached) remember(src);
        setStatus({ src, loaded: cached || shown.has(src), failed: false });
    }, [src]);

    useEffect(() => (still ? undefined : observeInView(wrap.current, setLive, '0px')), [still]);

    const done = (event) => {
        if (event.currentTarget !== img.current) return;
        remember(src);
        setStatus({ src, loaded: true, failed: false });
        onLoad?.(event);
    };
    const fail = (event) => {
        if (event.currentTarget !== img.current) return;
        shown.delete(src);
        setStatus({ src, loaded: false, failed: true });
        onError?.(event);
    };

    return (
        <span
            ref={wrap}
            className={`living-img ${still ? 'is-still' : ''} ${loaded ? 'is-loaded' : ''} ${live ? 'is-live' : ''} ${className}`}
            style={style}
        >
            {failed ? <span className="living-img__fallback" role="img" aria-label={alt || 'Image unavailable'}>
                <span aria-hidden>🌿</span>
                {alt && <small>{alt}</small>}
            </span> : <img
                key={src}
                ref={img}
                src={src}
                alt={alt}
                loading={eager ? 'eager' : 'lazy'}
                decoding="async"
                onLoad={done}
                onError={fail}
                className={`living-img__media ${imgClassName}`}
                style={{ objectFit: fit }}
                {...rest}
            />}
            {!still && !failed && <span className="living-img__sheen" aria-hidden />}
        </span>
    );
}
